// Per-university visual themes. Scoped: only universities listed here are themed;
// every other university keeps the platform default look untouched.
// CSS variable slots (set via rootStyle): --uni-primary, --uni-primary-dark, --uni-ink, --uni-accent, --uni-paper.

const wm = (svg) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

const georgiaWatermark = wm(
  `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>
    <g fill='none' stroke='#C8102E' stroke-opacity='0.05' stroke-width='6'>
      <path d='M60 18 v22 M60 80 v22 M18 60 h22 M80 60 h22'/>
      <path d='M52 22 h16 M52 38 h16 M22 52 v16 M38 52 v16 M52 82 h16 M52 98 h16 M82 52 v16 M98 52 v16'/>
    </g>
  </svg>`
);

const romaniaWatermark = wm(
  `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>
    <path d='M60 20 L100 60 L60 100 L20 60 Z M60 35 L85 60 L60 85 L35 60 Z'
      fill='none' stroke='#002B7F' stroke-opacity='0.05' stroke-width='5'/>
  </svg>`
);

const jordanWatermark = wm(
  `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>
    <path d='M60.0,22.0 L67.4,44.7 L89.7,36.3 L76.6,56.2 L97.0,68.5 L73.3,70.6 L76.5,94.2 L60.0,77.0 L43.5,94.2 L46.7,70.6 L23.0,68.5 L43.4,56.2 L30.3,36.3 L52.6,44.7 Z'
      fill='none' stroke='#007A3D' stroke-opacity='0.05' stroke-width='5'/>
  </svg>`
);

const rootStyle = (t, watermark) => ({
  "--uni-primary": t.primaryColor,
  "--uni-primary-dark": t.primaryDark,
  "--uni-ink": t.inkColor,
  "--uni-accent": t.accentColor,
  "--uni-paper": t.paperColor,
  backgroundColor: t.paperColor,
  backgroundImage: watermark,
  backgroundSize: "120px 120px",
});

const georgiaBase = {
  primaryColor: "#C8102E",
  primaryDark: "#9B0C22",
  inkColor: "#1A1A2E",
  accentColor: "#C9A227",
  paperColor: "#FBF8F3",
  ornament: "cross",
};

const romaniaBase = {
  primaryColor: "#002B7F",
  primaryDark: "#001B52",
  inkColor: "#14213D",
  accentColor: "#FCD116",
  paperColor: "#F7F9FC",
  ornament: "tricolor",
};

const jordanBase = {
  primaryColor: "#007A3D",
  primaryDark: "#00552B",
  inkColor: "#171717",
  accentColor: "#CE1126",
  paperColor: "#FAF8F2",
  ornament: "star7",
};

const makeTheme = (id, base, monogram, displayName, extra = {}) => ({
  id,
  monogram,
  displayName,
  ...base,
  rootStyle: rootStyle(base, extra.watermark),
  ...extra,
});

const brand = (primary, dark, accent, paper = "#FAFAF7") => ({
  primaryColor: primary, primaryDark: dark, inkColor: "#171721", accentColor: accent, paperColor: paper,
});

export const universityThemes = {
  UG_TBILISI: makeTheme("UG_TBILISI", georgiaBase, "UG", "University of Georgia", { watermark: georgiaWatermark }),
  IASI_UNI: makeTheme("IASI_UNI", romaniaBase, "UMF", "Grigore T. Popa University", { watermark: romaniaWatermark }),
  JUST_IRBID: makeTheme("JUST_IRBID", jordanBase, "JUST", "Jordan University of Science & Technology", { watermark: jordanWatermark }),
  JORDAN_UNI: makeTheme("JORDAN_UNI", jordanBase, "UJ", "University of Jordan", { watermark: jordanWatermark }),
  AHLIYYA_AMMAN: makeTheme("AHLIYYA_AMMAN", jordanBase, "AAU", "Al-Ahliyya Amman University", { watermark: jordanWatermark }),
  UJ_AMMAN: makeTheme("UJ_AMMAN", jordanBase, "UJ", "الجامعة الأردنية", { watermark: jordanWatermark }),
  AAU_AMMAN: makeTheme("AAU_AMMAN", jordanBase, "AAU", "جامعة عمان الأهلية", { watermark: jordanWatermark, coursePrefixes: ["AAU"] }),
  NAJAH: makeTheme("NAJAH", brand("#00703C", "#004D29", "#C8102E", "#F6FAF5"), "NNU", "An-Najah National University"),
  NVU: makeTheme("NVU", brand("#003057", "#001F3A", "#C9A227", "#F5F8FB"), "NVU", "New Vision University"),
  NVU_NEW_VISION: makeTheme("NVU_NEW_VISION", brand("#003057", "#001F3A", "#C9A227", "#F5F8FB"), "NVU", "New Vision University"),
  SAPIENZA_ROMA: makeTheme("SAPIENZA_ROMA", brand("#822433", "#5C1823", "#C9A227", "#FBF7F5"), "SAP", "Sapienza University of Rome"),
  MILANO_IMS: makeTheme("MILANO_IMS", brand("#003DA5", "#002B73", "#FDBB30", "#F5F8FC"), "IMS", "University of Milan"),
  BOLOGNA: makeTheme("BOLOGNA", brand("#A6192E", "#77121F", "#C9A227", "#FBF6F4"), "UNIBO", "University of Bologna"),
  SEMMELWEIS_UNI: makeTheme("SEMMELWEIS_UNI", brand("#0D4880", "#093457", "#8A8D8F", "#F5F8FB"), "SE", "Semmelweis University"),
  DEBRECEN_UNI: makeTheme("DEBRECEN_UNI", brand("#1B3F94", "#122B66", "#FDBB30", "#F6F8FC"), "UD", "University of Debrecen"),
  SZEGED_UNI: makeTheme("SZEGED_UNI", brand("#003F87", "#002B5E", "#C9A227", "#F5F8FB"), "SZTE", "University of Szeged"),
  JAGIELLONIAN_UNI: makeTheme("JAGIELLONIAN_UNI", brand("#003D7C", "#002A56", "#B99351", "#F6F8FB"), "UJ", "Jagiellonian University"),
  WARSAW_MED_UNI: makeTheme("WARSAW_MED_UNI", brand("#A6093D", "#75062B", "#8A8D8F", "#FBF5F7"), "WUM", "Medical University of Warsaw"),
  POZNAN_MED_UNI: makeTheme("POZNAN_MED_UNI", brand("#005EB8", "#004284", "#C8102E", "#F5F8FC"), "PUMS", "Poznan University of Medical Sciences"),
  GDANSK_MED_UNI: makeTheme("GDANSK_MED_UNI", brand("#9D2235", "#701825", "#C9A227", "#FBF6F6"), "GUMed", "Medical University of Gdansk"),
  ANKARA_UNI: makeTheme("ANKARA_UNI", brand("#002855", "#001B3A", "#C8102E", "#F5F7FB"), "AU", "Ankara University"),
  MARMARA_UNI: makeTheme("MARMARA_UNI", brand("#003366", "#002347", "#7A1F3D", "#F5F7FB"), "MU", "Marmara University"),
  NTSUMP: makeTheme("NTSUMP", brand("#005BBB", "#004083", "#FFD500", "#F5F8FC"), "NUPh", "National University of Pharmacy"),
  AJMAN_UNI: makeTheme("AJMAN_UNI", brand("#00447C", "#002F57", "#C9A227", "#F5F8FB"), "AU", "Ajman University"),
  CHARLES_PRAGUE: makeTheme("CHARLES_PRAGUE", brand("#862633", "#5E1A24", "#C9A227", "#FBF7F4"), "CU", "Charles University"),
  CIU_TBILISI: makeTheme("CIU_TBILISI", brand("#C8102E", "#8F0B20", "#1A1A2E", "#FBF5F5"), "CIU", "Caucasus International University", { watermark: georgiaWatermark }),
  HASHEMITE_UNI: makeTheme("HASHEMITE_UNI", brand("#7A1F3D", "#5A1229", "#C9A227", "#FBF6F7"), "HU", "The Hashemite University", { watermark: jordanWatermark }),
  MUTAH_UNI: makeTheme("MUTAH_UNI", brand("#1B5E20", "#104015", "#C9A227", "#F4FAF5"), "MU", "Mutah University", { watermark: jordanWatermark }),
  YARMOUK_UNI: makeTheme("YARMOUK_UNI", brand("#1E7A34", "#145223", "#C9A227", "#F4FAF6"), "YU", "Yarmouk University", { watermark: jordanWatermark }),
  ASU_AMMAN: makeTheme("ASU_AMMAN", brand("#0F4C9C", "#0A3369", "#D64545", "#F4F7FC"), "ASU", "Applied Science Private University", { watermark: jordanWatermark }),
  ZARQA_UNI: makeTheme("ZARQA_UNI", brand("#005BAA", "#003E75", "#F28C28", "#F4F8FC"), "ZU", "Zarqa University", { watermark: jordanWatermark }),
  MEU_AMMAN: makeTheme("MEU_AMMAN", brand("#1F3A93", "#142664", "#D64545", "#F5F6FB"), "MEU", "Middle East University", { watermark: jordanWatermark }),
  AMMAN_ARAB_UNI: makeTheme("AMMAN_ARAB_UNI", brand("#14427D", "#0D2C55", "#C9A227", "#F4F7FB"), "AAU", "Amman Arab University", { watermark: jordanWatermark }),
  AMSU_AQABA: makeTheme("AMSU_AQABA", brand("#0E7490", "#095264", "#1A1A2E", "#F2FAFB"), "AMSU", "Aqaba Medical Sciences University", { watermark: jordanWatermark }),
  UNICAMILLUS_ROMA: makeTheme("UNICAMILLUS_ROMA", brand("#003057", "#00203A", "#38B6E3", "#F4F8FC"), "UC", "UniCamillus International Medical University"),
  TOR_VERGATA: makeTheme("TOR_VERGATA", brand("#007A53", "#005238", "#C9A227", "#F3FAF7"), "TV", "University of Rome Tor Vergata"),
  SIENA_UNI: makeTheme("SIENA_UNI", brand("#B00020", "#7C0016", "#C9A227", "#FBF5F5"), "US", "University of Siena"),
  HUMANITAS_MILAN: makeTheme("HUMANITAS_MILAN", brand("#1B365D", "#122540", "#F28C28", "#F4F7FB"), "HU", "Humanitas University"),
  HACETTEPE_UNI: makeTheme("HACETTEPE_UNI", brand("#B61F24", "#821317", "#1A1A2E", "#FBF5F5"), "HÜ", "Hacettepe University"),
};

export function getUniversityTheme(externalId) {
  return universityThemes[externalId] || null;
}

// Course ids are prefixed with the university external_id (e.g. "UG_TBILISI_MED_...").
// Some catalogs use a short prefix (e.g. AAU_AMMAN courses -> "AAU_...") declared via coursePrefixes.
export function getUniversityThemeForCourseId(courseId) {
  if (!courseId) return null;
  for (const theme of Object.values(universityThemes)) {
    const prefixes = [theme.id, ...(theme.coursePrefixes || [])];
    for (const p of prefixes) {
      if (courseId === p || courseId.startsWith(p + "_")) return theme;
    }
  }
  return null;
}
